import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FolderCheck, Search, Users, AlertTriangle, Clock, Receipt, FileWarning, UserPlus } from "lucide-react";
import { api } from "../lib/api";
import { PageSkeleton } from "../components/PageSkeleton";
import { HrTodoList } from "../components/hr/HrTodoList";
import { EmployeeHealthCard } from "../components/hr/EmployeeHealthCard";
import { EmployeeFile } from "../components/hr/EmployeeFile";
import { OnboardingWizard } from "../components/hr/OnboardingWizard";

const Stat = ({ icon: Icon, label, value, tone, testId }) => (
  <div data-testid={testId} className={`flex items-center gap-2 h-9 px-3 ring-1 rounded-full text-[11px] font-mono ${tone}`}>
    <Icon className="w-3.5 h-3.5" />{label} <b className="font-black text-sm">{value}</b>
  </div>
);

export default function HRPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [highlightDoc, setHighlightDoc] = useState(null);
  const selectedId = params.get("employee");
  const wizard = params.get("nowy") === "1";
  const setWizard = (on) => setParams((p) => { const n = new URLSearchParams(p); if (on) { n.set("nowy", "1"); n.delete("employee"); } else n.delete("nowy"); return n; });

  const load = useCallback(() => api.get("/hr/overview").then((r) => setData(r.data)).catch(() => setData((d) => d || { summary: {}, employees: [], todos: [] })), []);
  useEffect(() => { load(); }, [load]);

  const open = (id, docType = null) => {
    setHighlightDoc(docType);
    setParams((p) => { const n = new URLSearchParams(p); n.delete("nowy"); if (id) n.set("employee", id); else n.delete("employee"); return n; });
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.employees.filter((r) => {
      const name = `${r.employee.first_name} ${r.employee.last_name} ${r.employee.position || ""}`.toLowerCase();
      if (needle && !name.includes(needle)) return false;
      if (filter === "issues") return r.todo_count > 0;
      if (filter === "urgent") return r.worst_urgency != null && r.worst_urgency <= 1;
      if (filter === "complete") return r.todo_count === 0 && r.health != null;
      return true;
    });
  }, [data, q, filter]);

  if (!data) return <PageSkeleton variant="grid" label="Ładowanie kadr…" />;
  const s = data.summary;

  return (
    <div className="p-3 md:p-6" data-testid="hr-page">
      <div className="border border-zinc-900 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 md:px-5 py-4 border-b border-zinc-200 bg-zinc-50">
          <div>
            <div className="font-label mb-1">// Kadry · teczki pracowników · pilnowanie terminów</div>
            <h1 className="font-heading font-black text-xl md:text-2xl uppercase tracking-tight flex items-center gap-2"><FolderCheck className="w-6 h-6 text-brand" />Kadry</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setWizard(true)} data-testid="hr-new-employee" className="h-9 px-4 inline-flex items-center gap-2 bg-brand text-white text-xs font-heading font-black uppercase tracking-wider hover:bg-zinc-900 transition-colors"><UserPlus className="w-4 h-4" />Nowy pracownik</button>
            <Stat icon={Users} label="Pracownicy" value={s.employees} tone="bg-white text-zinc-700 ring-zinc-200" testId="hr-stat-employees" />
            <Stat icon={AlertTriangle} label="Pilne" value={s.expired} tone={s.expired ? "bg-red-50 text-red-700 ring-red-200" : "bg-white text-zinc-500 ring-zinc-200"} testId="hr-stat-expired" />
            <Stat icon={Clock} label="Wkrótce" value={s.expiring} tone={s.expiring ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-white text-zinc-500 ring-zinc-200"} testId="hr-stat-expiring" />
            <Stat icon={Receipt} label="Rachunki" value={s.rachunki} tone={s.rachunki ? "bg-indigo-50 text-indigo-800 ring-indigo-200" : "bg-white text-zinc-500 ring-zinc-200"} testId="hr-stat-rachunki" />
            <Stat icon={FileWarning} label="Braki" value={s.missing} tone="bg-white text-zinc-700 ring-zinc-200" testId="hr-stat-missing" />
          </div>
        </div>

        {wizard ? (
          <div className="p-4 md:p-6">
            <OnboardingWizard onClose={(empId) => (empId ? open(empId) : setWizard(false))} onDone={load} />
          </div>
        ) : selectedId ? (
          <div className="p-4 md:p-6">
            <EmployeeFile key={selectedId} employeeId={selectedId} highlightDoc={highlightDoc} onBack={() => open(null)} onChanged={load} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr]">
            <aside className="border-b lg:border-b-0 lg:border-r border-zinc-200 lg:max-h-[calc(100vh-220px)] lg:overflow-y-auto">
              <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
                <div><div className="font-label">// Lista kadrowej</div><h2 className="font-heading font-black uppercase tracking-tight">Do zrobienia</h2></div>
                <span data-testid="hr-todo-count" className="font-mono text-sm font-black">{data.todos.length}</span>
              </div>
              <HrTodoList todos={data.todos} onOpen={open} />
            </aside>
            <main className="p-4 md:p-5 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <label className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj pracownika…" data-testid="hr-search" className="w-full h-10 pl-9 pr-3 border border-zinc-300 text-sm outline-none focus:border-zinc-900" />
                </label>
                <div className="flex ring-1 ring-zinc-300 text-[11px] font-bold">
                  {[["all", "Wszyscy"], ["urgent", "Pilne"], ["issues", "Z brakami"], ["complete", "Komplet"]].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`hr-filter-${k}`} className={`h-10 px-3 transition-colors ${filter === k ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-100"}`}>{l}</button>
                  ))}
                </div>
              </div>
              {rows.length === 0 ? (
                <div data-testid="hr-employees-empty" className="text-sm text-zinc-500 p-6 text-center">Brak pracowników spełniających filtr. Pracowników dodasz w module <b>Pracownicy</b>.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 stagger" data-testid="hr-employee-grid">
                  {rows.map((r) => <EmployeeHealthCard key={r.employee.id} row={r} onOpen={open} />)}
                </div>
              )}
            </main>
          </div>
        )}
        <div className="px-4 md:px-5 py-3 border-t border-zinc-200 bg-zinc-50 text-[11px] font-mono text-zinc-500 flex flex-wrap gap-4">
          <span>// Ostrzeżenie 30 dni przed końcem ważności · rachunki UZ sprawdzane co miesiąc · status studenta do 26. urodzin</span>
        </div>
      </div>
    </div>
  );
}
